import React, { Fragment, useState } from "react";
import QurealSection from "../common/QurealSection";
import SectionHeader from "../components/commons/SectionHeader";
import { Button, Checkbox, Segmented, Select, Upload, message } from "antd";
import styles from "./styles/AIImageGen.module.css";
import classNames from "classnames";
import { startCase } from "lodash";
import { ArrowRightOutlined, UploadOutlined } from "@ant-design/icons";
import { useAIStudioPerson } from "../_actions/bot";
import log from "cslog";

const DEFAULT_CONFIG = {
    size: "portrait_long",
    gender: "female",
    reference_image: null,
};

const AP_MAPPING = {
    square: "1/1",
    portrait: "3/4",
    portrait_long: "9/16",
    landscape: "4/3",
    landscape_long: "16/9",
};

function PersonStudioInputs({ config, setConfig }) {
    return (
        <Fragment>
            <p className={styles.label}>Reference Image (Optional)</p>

            <Upload
                accept="image/*"
                maxCount={1}
                showUploadList={true}
                beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        setConfig({
                            ...config,
                            reference_image: reader.result,
                        });
                    };
                    reader.readAsDataURL(file);

                    // stop auto upload
                    return false;
                }}
                onRemove={() =>
                    setConfig({
                        ...config,
                        reference_image: null,
                    })
                }
            >
                <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>

            <p className={styles.label}>Aspect Ratio</p>
            <AspectRatioPicker
                value={config.size}
                onChange={(value) => setConfig({ ...config, size: value })}
            />

            <p className={styles.label}>Gender</p>
            <GenderPicker
                value={config.gender}
                onChange={(value) =>
                    setConfig({ ...config, gender: value, accessories: {} })
                }
            />

            <p className={styles.label}>Age</p>
            <AgePicker
                options={[
                    "infant",
                    "child",
                    "teen",
                    "adult",
                    "middle aged",
                    "elderly",
                ]}
                value={config.age}
                onChange={(value) => setConfig({ ...config, age: value })}
            />

            <p className={styles.label}>Skin Tone</p>
            <ColorPicker
                colors={[
                    { name: "Pale", hex: "#FFDFC4" },
                    { name: "Fair", hex: "#F0D5BE" },
                    { name: "Light", hex: "#E6BEA5" },
                    { name: "Medium", hex: "#C68642" },
                    { name: "Olive", hex: "#8D5524" },
                    { name: "Brown", hex: "#7D4E24" },
                    { name: "Dark", hex: "#3D1E10" },
                ]}
                value={config.skin_tone}
                onChange={(value) => setConfig({ ...config, skin_tone: value })}
            />

            {/* <p className={styles.label}>Style Theme</p>
                <Select
                    style={{ width: "100%" }}
                    options={[
                        "professional", "traditional", "modern", "royal", "casual", "elegant", "bohemin", "medieval", "futuristic", "ancient"
                    ].map(item => ({ value: item, label: startCase(item) }))}
                    value={config.style}
                    onChange={(value) => setConfig({ ...config, style: value })}
                    allowClear
                /> */}

            <div className={styles.two_col}>
                <div>
                    <p className={styles.label}>Hair Style</p>
                    <Select
                        style={{ width: "100%" }}
                        options={[
                            "straight",
                            "wavy",
                            "curly",
                            "braided",
                            "ponytail",
                            //  "bun",
                            "buzz cut",
                            "spiky",
                            "afro",
                            "dreadlocks",
                            "bald",
                        ].map((item) => ({
                            value: item,
                            label: startCase(item),
                        }))}
                        value={config.hair_style}
                        onChange={(value) =>
                            setConfig({ ...config, hair_style: value })
                        }
                        allowClear
                    />
                </div>

                <div>
                    <p className={styles.label}>Hair Length</p>
                    <Select
                        style={{ width: "100%" }}
                        options={[
                            { value: "short", label: "Short" },
                            { value: "medium", label: "Medium" },
                            { value: "long", label: "Long" },
                            { value: "bald", label: "Bald" },
                        ]}
                        value={config.hair_length}
                        onChange={(value) =>
                            setConfig({ ...config, hair_length: value })
                        }
                        allowClear
                    />
                </div>
            </div>

            <p className={styles.label}>Hair Color</p>
            <ColorPicker
                colors={[
                    { name: "Black", hex: "#2C2C2C" },
                    { name: "Brown", hex: "#6F4E37" },
                    { name: "Blonde", hex: "#F2D16B" },
                    { name: "Red", hex: "#C04000" },
                    { name: "Grey", hex: "#B2B2B2" },
                    { name: "White", hex: "#FFFFFF" },
                    { name: "Auburn", hex: "#A52A2A" },
                ]}
                value={config.hair_color}
                onChange={(value) =>
                    setConfig({ ...config, hair_color: value })
                }
            />

            <p className={styles.label}>Eye Color</p>
            <ColorPicker
                tiny={true}
                colors={[
                    { name: "Black", hex: "#000000" },
                    { name: "Brown", hex: "#654321" },
                    { name: "Blue", hex: "#0000FF" },
                    { name: "Green", hex: "#008000" },
                    { name: "Hazel", hex: "#B57E2A" },
                    { name: "Grey", hex: "#808080" },
                    { name: "White", hex: "#FFFFFF" },
                ]}
                value={config.eye_color}
                onChange={(value) => setConfig({ ...config, eye_color: value })}
            />

            <p className={styles.label}>Clothing Style</p>
            <Select
                style={{ width: "100%" }}
                options={[
                    "casual",
                    "formal",
                    "business",
                    "sporty",
                    "party",
                    "streetwear",
                    "traditional",
                ].map((item) => ({ value: item, label: startCase(item) }))}
                value={config.cloth_style}
                onChange={(value) =>
                    setConfig({ ...config, cloth_style: value })
                }
                allowClear
            />

            <p className={styles.label}>Clothing Color</p>
            <ColorPicker
                tiny={true}
                colors={COLORS_ARR.map((color) => ({
                    name: color,
                    hex: color,
                }))}
                value={config.cloth_color}
                onChange={(value) =>
                    setConfig({ ...config, cloth_color: value })
                }
            />

            <p className={styles.label}>Facial Expression</p>
            <FacialExpressionPicker
                value={config.expression}
                onChange={(value) =>
                    setConfig({ ...config, expression: value })
                }
            />

            <p className={styles.label}>Shot Type</p>
            <Select
                style={{ width: "100%" }}
                options={[
                    { value: "waist up", label: "Waist up" },
                    { value: "full body", label: "Full body" },
                    { value: "headshot", label: "Headshot" },
                ]}
                value={config.shot_type}
                onChange={(value) => setConfig({ ...config, shot_type: value })}
                allowClear
            />

            <p className={styles.label}>Pose</p>
            <PosePicker
                value={config.pose}
                onChange={(value) => setConfig({ ...config, pose: value })}
            />

            <p className={styles.label}>Ethnicity</p>
            <Select
                style={{ width: "100%" }}
                options={[
                    { value: "indian", label: "Indian" },
                    { value: "asian", label: "Asian" },
                    { value: "south asian", label: "South Asian" },
                    { value: "caucasian", label: "Caucasian" },
                    { value: "african", label: "African" },
                    { value: "latino", label: "Latino" },
                    { value: "middle_eastern", label: "Middle Eastern" },
                    { value: "native_american", label: "Native American" },
                ]}
                value={config.ethnicity}
                onChange={(value) => setConfig({ ...config, ethnicity: value })}
                allowClear
            />

            <p className={styles.label}>Accessories</p>
            <AcceseriesPicker
                gender={config.gender}
                value={config.accessories || {}}
                onChange={(value) =>
                    setConfig({ ...config, accessories: value })
                }
            />

            <div className={styles.sidebar_bottom_gap}></div>
        </Fragment>
    );
}

export default PersonStudioInputs;

function FacialExpressionPicker({ value, onChange }) {
    return (
        <div className={styles.expression_wrapper}>
            {[
                { value: "neutral", icon: "😐" },
                { value: "happy", icon: "😊" },
                { value: "sad", icon: "😢" },
                { value: "angry", icon: "😡" },
                { value: "surprised", icon: "😲" },
                { value: "smiling", icon: "😁" },
                { value: "disgusted", icon: "🤢" },
                { value: "fearful", icon: "😨" },
                { value: "confused", icon: "😕" },
            ].map((item) => {
                return (
                    <div
                        className={classNames({
                            [styles.selected]: value === item.value,
                        })}
                        onClick={() => onChange(item.value)}
                    >
                        {item.icon}
                    </div>
                );
            })}
        </div>
    );
}

function AgePicker({ options, value, onChange }) {
    return (
        <div className={styles.text_options_wrapper}>
            {options.map((option) => {
                return (
                    <div
                        className={classNames(styles.text_option, {
                            [styles.selected]: value === option,
                        })}
                        onClick={() => onChange(option)}
                    >
                        {startCase(option)}
                    </div>
                );
            })}
        </div>
    );
}

function ColorPicker({ colors, value, onChange, tiny = false }) {
    return (
        <div className={tiny ? styles.grid_tiny : styles.grid_small}>
            {colors.map((color) => {
                return (
                    <div
                        className={classNames(
                            tiny ? styles.grid_item_tiny : styles.grid_item,
                            { [styles.selected]: color.name === value }
                        )}
                        style={{ backgroundColor: color.hex }}
                        onClick={() => onChange(color.name)}
                    ></div>
                );
            })}
        </div>
    );
}

function GenderPicker({ value, onChange }) {
    return (
        <div className={styles.grid_wrapper}>
            <div
                className={classNames(styles.grid_item, {
                    [styles.selected]: value === "male",
                })}
                onClick={() => onChange("male")}
            >
                <i
                    className={classNames(styles.gender_icon, "fas fa-mars")}
                    style={{ color: "#1E90FF" }}
                ></i>
            </div>
            <div
                className={classNames(styles.grid_item, {
                    [styles.selected]: value === "female",
                })}
                onClick={() => onChange("female")}
            >
                <i
                    className={classNames(styles.gender_icon, "fas fa-venus")}
                    style={{ color: "#FF69B4" }}
                ></i>
            </div>
        </div>
    );
}

export function AspectRatioPicker({ value, onChange }) {
    return (
        <div className={styles.ar_wrapper}>
            {[
                { name: "square", width: 30, height: 30 },
                { name: "portrait", width: 30, height: 40 },
                { name: "portrait_long", width: 23, height: 40 },
                { name: "landscape", width: 40, height: 30 },
                { name: "landscape_long", width: 40, height: 23 },
            ].map((ap) => {
                return (
                    <div
                        className={classNames(styles.ar_item, {
                            [styles.selected]: value === ap.name,
                        })}
                        onClick={() => onChange(ap.name)}
                    >
                        <div
                            className={styles.ar_box}
                            style={{ width: ap.width, height: ap.height }}
                        ></div>
                    </div>
                );
            })}
        </div>
    );
}

function PosePicker({ value, onChange }) {
    return (
        <Select
            style={{ width: "100%" }}
            options={POSES.map((item) => ({
                value: item,
                label: startCase(item),
            }))}
            value={value}
            onChange={onChange}
            allowClear
        />
    );
}

function AcceseriesPicker({ gender, value, onChange }) {
    const accessories =
        gender === "male" ? ACCESSORIES_MALE : ACCESSORIES_FEMALE;
    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            {accessories.map((accessory) => {
                return (
                    <Checkbox
                        checked={value?.[accessory]}
                        onChange={(e) =>
                            onChange({
                                ...value,
                                [accessory]: e.target.checked,
                            })
                        }
                    >
                        {startCase(accessory)}
                    </Checkbox>
                );
            })}
            {/* <Checkbox checked={value?.glasses} onChange={(e) => onChange({ ...value, glasses: e.target.checked })}>Glasses</Checkbox>
        <Checkbox>Mustages</Checkbox> */}
        </div>
    );
}

const ACCESSORIES_MALE = [
    "glasses",
    "mustache",
    "beard",
    "hat",
    "gold chain",
    "scarf",
    "tie",
    "watch",
    "belt",
];
const ACCESSORIES_FEMALE = [
    "glasses",
    "earrings",
    "necklace",
    "bracelets",
    "hat",
    "scarf",
    "watch",
    "belt",
];

const COLORS_ARR = [
    "red",
    "orangered",
    "orange",
    "gold",
    "yellow",
    "yellowgreen",
    "green",
    "seagreen",
    "teal",
    "cyan",
    "skyblue",
    "blue",
    "royalblue",
    "indigo",
    "violet",
    "purple",
    "magenta",
    "pink",
    "lightpink",
    "brown",
    "tan",
    "beige",
    "lightgray",
    "gray",
    "darkgray",
    "black",
    "white",
];

const POSES = [
    "crossed arms",
    "presenting with open arms",
    "hands in pockets",
    "hand on mouth",
    "hand on hip",
    "hands on waist",
    "jumping",
    "head tilt",
    "leaning forward",
    "arms behind head",
    "arms behind back",
    "hands up",
    "hair pull",
    "love symbol",
    "spread arms",
    "waving",
    "finger gun",
    "thumbs up",
    "salute",
    "sitting on chair",
    "sitting on chair leaning on desk",
    "holding cup",
    "holding paper",
    "open coat",
    "bend over",
    "sitting in lotus position",
    "lying on back",
    "crossing ankles",
    "chin in hand",
    "over the shoulder",
    "legs crossed",
    "strolling",
    "one leg up",
    "adjusting jacket collar",
    "jacket over the shoulder",
    "holding cufflinks",
    "thoughtful",
    "one hand in pocket",
    "reading",
    "hugging a toy",
    "resting head on hands",
    "wrapping hands around legs",
    "facing away from camera",
    "dancing",
    "follow me",
    "lying in fetal position",
    "squatting",
];
